// Component: StuckOrdersPanel.jsx
// Purpose: 104 Element R (D67/R12) — the PRIMARY user-facing self-service recovery
//          screen. "A user by themselves should be able to close it... they cannot
//          afford to call us" (Deepak, D67). Lists the caller's OWN live orders that
//          have not reached a terminal broker outcome (instrument, side, quantity,
//          paper/live + account attribution, and status), and lets the user
//          force-close or repair-netting them directly — never routes through admin.
//
//! R12.5/D126 — the real ambiguous-resolution predicate now exists on the SAME
//! GET /orders/consolidated call (services.trading.getLiveOrders, unchanged —
//! no new route). The existing fields are now sourced from the persisted
//! `orders.ambiguity_*` annotation. Each order row carries `ambiguous_status`: 'pending' or
//! 'unresolved' means genuinely stuck; 'resolved_filled'/'resolved_rejected'/
//! 'resolved_open' means resolved history, NOT stuck; `null` means the order
//! never entered the ambiguous-resolution path at all, i.e. healthy. The
//! envelope also carries `ambiguous_state_degraded` (true only when the
//! backend's ambiguous-resolution read itself failed) — DISTINCT from a row's
//! `null`, which is a verified-healthy read, not a missing one. When degraded,
//! every row's ambiguous_status fail-safes to null server-side, so the panel
//! must check the envelope flag FIRST — otherwise a degraded read is
//! indistinguishable from "nothing stuck" (see the fail-safe-not-fail-loud
//! note in backend/order-view/app/services/consolidated_view.py:516-522).

import React, { useEffect, useState, useCallback } from 'react';
import { PageHeader, Button, EmptyState, Loader, Icon } from '../../../components/ds';
import StatusBadge from '../../../components/trading/StatusBadge';
import { ModeBadge } from '../../../components/trading/OrdersTable';
import RecoveryActionModal from '../../../components/trading/RecoveryActionModal';
import { rejectReasonLabel } from '../../../utils/rejectReason';
import { getSourceTagBadge, resolveSourceTag, getBrokerBadge } from '../../../utils/tradingTransforms';
import { services } from '../../../services/api';
import { useSnackbar } from '../../../contexts/SnackbarContext';

//--> The real ambiguous-resolution predicate (R12.5/D126) — see the header note.
const STUCK_AMBIGUOUS_STATUSES = new Set(['pending', 'unresolved']);

//--> A row is actionable here only for LIVE orders (paper is simulated — no broker
//    account to recover; reference_paper_vs_live_fill_paths_and_component_scope.md).
const isStuckLiveOrder = (order) =>
  (order.execution_mode || '').toLowerCase() === 'live' &&
  STUCK_AMBIGUOUS_STATUSES.has((order.ambiguous_status || '').toLowerCase());

//--> Human-readable presentation of a machine-terse `ambiguous_last_error` string —
//    a stressed retail trader reads this, not an engineer.
const lastErrorLabel = (order) => {
  if (!order.ambiguous_last_error) return null;
  return order.ambiguous_last_error;
};

//--> Human instrument label — never a raw UUID on this surface (R101/R12.5's own AC).
const instrumentLabel = (order) =>
  order.display_symbol || order.symbol || (order.instrument_id ? 'Unresolved instrument' : 'Unknown instrument');

//--> Human account label (L-23) — never the raw `account_id` UUID on this surface. Mirrors
//    the schema's own documented convention (backend/order-view/app/schemas/trading_view.py's
//    ConsolidatedRow D2 note: "account_id UUID is NOT surfaced. Use broker + broker_client_id
//    as the human-readable account identifier. If broker_client_id is None ... the broker
//    label still shows on the row.") — exactly this panel's case: these are OMS/ALGO leg rows
//    (execution_mode=live, ambiguous_status pending/unresolved) which never carry
//    broker_client_id (order-view's _map_oms_order_to_row hard-sets it None; only the BD/
//    position legs populate it, per consolidated_view.py:207). Same fallback chain
//    OrderDetailDrawer.jsx:481 uses for its own "Account" field, extended with the
//    already-imported getBrokerBadge (tradingTransforms.js) label so the two account
//    surfaces in the app agree on wording. Never fabricate, never truncate the UUID.
const accountLabel = (order, brokerBadge) => {
  const brokerName = brokerBadge?.label || order.broker;
  if (!brokerName) return 'Unattributed';
  return order.broker_client_id ? `${brokerName} · ${order.broker_client_id}` : brokerName;
};

const StuckOrdersPanel = () => {
  /*>>> State <<<*/
  //! Four distinct states — 'degraded' is not a variant of 'ready'. Collapsing
  //! it into "nothing stuck" is exactly the silent false-negative this screen
  //! must never produce on a money surface (R8.8).
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'degraded' | 'error'
  const [rows, setRows] = useState([]);
  const [actionTarget, setActionTarget] = useState(null); // { order, mode }
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState(null);
  const { showSnackbar } = useSnackbar();

  /*>>> Effects — fetch <<<*/
  const load = useCallback(async () => {
    setStatus('loading');
    try {
      //--> 7-day window — a stuck order can predate "today" (cross-day recovery,
      //    the same class Element M/S handle on the exit side).
      const to = new Date();
      const from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
      const res = await services.trading.getLiveOrders({
        from_dt: from.toISOString(),
        to_dt: to.toISOString(),
      });
      const envelope = res?.data?.data || res?.data || {};
      const orders = envelope.orders || [];
      setRows(orders.filter(isStuckLiveOrder));
      //! Check the envelope-level flag FIRST — a degraded read fail-safes every
      //! row's ambiguous_status to null, so without this check a degraded read
      //! is silently indistinguishable from "nothing stuck".
      setStatus(envelope.ambiguous_state_degraded ? 'degraded' : 'ready');
    } catch (err) {
      console.log('[DIAG] StuckOrdersPanel — load failed', err);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /*>>> Handlers <<<*/
  const openAction = (order, mode) => {
    setActionError(null);
    setActionTarget({ order, mode });
  };

  const closeAction = () => {
    setActionTarget(null);
    setActionError(null);
  };

  const confirmAction = async (form) => {
    if (!actionTarget) return;
    const { order, mode } = actionTarget;
    setSubmitting(true);
    setActionError(null);
    try {
      if (mode === 'force-close') {
        await services.selfServiceRecovery.forceClose(order.strategy_instance_id, {
          lifecycle_id: order.lifecycle_id,
          reason: form.reason || 'user_self_service_force_close',
          compliance_note: form.compliance_note,
          acknowledge_loss: true,
        });
      } else {
        await services.selfServiceRecovery.repairNetting(order.strategy_instance_id, {
          instrument_id: order.instrument_id,
          account_id: order.account_id,
          idempotency_key: `${order.strategy_instance_id}:${order.instrument_id}:${Date.now()}`,
          compliance_note: form.compliance_note,
          true_exit_price: form.true_exit_price,
          acknowledge_loss: true,
        });
      }
      showSnackbar('Recovery action submitted.', 'success');
      closeAction();
      load();
    } catch (err) {
      //--> Envelope error shape: { status: 'error', data: null, error: { code, message } }.
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.detail ||
        'Could not complete this action. Please try again.';
      setActionError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  /*>>> Render <<<*/
  return (
    <div className="px-4 md:px-6 pb-8">
      <PageHeader
        title="Stuck"
        accent="Orders"
        subtitle="Orders on your own broker accounts that have not yet confirmed — close or repair them yourself, no support call needed."
      />

      {status === 'loading' && (
        <div className="flex justify-center py-12">
          <Loader label="Checking your orders…" />
        </div>
      )}

      {status === 'error' && (
        <EmptyState
          icon={<Icon name="warn" />}
          title="Could not load your orders"
          description="Something went wrong reaching the order service. Please retry."
          action={<Button onClick={load}>Retry</Button>}
        />
      )}

      {status === 'degraded' && (
        <EmptyState
          icon={<Icon name="warn" />}
          title="Could not check for stuck orders"
          description="We could not verify whether any of your orders are stuck right now. This is not the same as 'nothing stuck' — please retry, and if this persists treat any unconfirmed order as needing your attention."
          action={<Button onClick={load}>Retry check</Button>}
        />
      )}

      {status === 'ready' && rows.length === 0 && (
        <EmptyState
          icon={<Icon name="success" />}
          title="Nothing stuck"
          description="All of your live orders have confirmed. There is nothing that needs your attention here."
        />
      )}

      {status === 'ready' && rows.length > 0 && (
        <ul className="flex flex-col gap-3" data-testid="stuck-orders-list">
          {rows.map((order) => {
            const sourceTag = getSourceTagBadge(resolveSourceTag(order));
            const brokerBadge = getBrokerBadge(order.broker);
            const isManual = String(order.strategy_instance_id || '').startsWith('manual:');
            const reason = rejectReasonLabel(order.reject_reason);
            return (
              <li
                key={order.order_id}
                data-testid="stuck-order-row"
                className="rounded-lg border border-line p-4 flex flex-col gap-2"
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{instrumentLabel(order)}</span>
                    <span className="text-sm text-fg-2 uppercase">{order.side || '—'}</span>
                    <span className="text-sm text-fg-2">Qty {order.qty ?? '—'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ModeBadge mode={(order.execution_mode || '').toUpperCase() === 'LIVE' ? 'LIVE' : 'PAPER'} />
                    <span className="text-xs px-2 py-1 rounded" style={{ background: 'var(--bg-2)' }}>
                      {sourceTag.label}
                    </span>
                    {brokerBadge && (
                      <span className="text-xs px-2 py-1 rounded" style={{ background: 'var(--bg-2)' }}>
                        {brokerBadge.label}
                      </span>
                    )}
                    <StatusBadge status={order.status} />
                  </div>
                </div>

                <div className="text-sm text-fg-2">
                  Account: {accountLabel(order, brokerBadge)}
                  {reason ? ` — ${reason}` : ' — Unconfirmed: no broker acknowledgment yet.'}
                </div>

                <div className="text-sm text-fg-2">
                  {order.ambiguous_attempt_count != null && (
                    <span>
                      {order.ambiguous_attempt_count === 1
                        ? '1 automatic resolution attempt made'
                        : `${order.ambiguous_attempt_count} automatic resolution attempts made`}
                      {order.ambiguous_attempt_count > 0 ? ' — did not resolve on its own.' : '.'}
                    </span>
                  )}
                  {lastErrorLabel(order) && (
                    <div className="text-warning">Last error: {lastErrorLabel(order)}</div>
                  )}
                </div>

                <div className="flex gap-2 pt-1">
                  <Button variant="danger" size="sm" onClick={() => openAction(order, 'force-close')}>
                    Force-close
                  </Button>
                  {isManual && (
                    <Button variant="secondary" size="sm" onClick={() => openAction(order, 'repair-netting')}>
                      Repair netting
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <RecoveryActionModal
        open={!!actionTarget}
        mode={actionTarget?.mode}
        instrumentLabel={actionTarget ? instrumentLabel(actionTarget.order) : ''}
        submitting={submitting}
        errorMessage={actionError}
        onClose={closeAction}
        onConfirm={confirmAction}
      />
    </div>
  );
};

export default StuckOrdersPanel;
